/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useRef, useState } from "react";
import { SearchFilterTitle } from "../components/reports/search-filter-title";
import { useTranslation } from "react-i18next";
import { Grid, MenuItem, TextField } from "@material-ui/core";
import { FormInputLabel } from "../components/common/FormInputLabel";
import { useFfmcentersHandler } from "../hooks/rest/use-ffmcenters-handler";
import { useStoresHandler } from "../hooks/rest/use-stores-handler";
import { Button, RequiredLabel } from "../components";
import {
  FulfillmentCenter,
  Store,
  useInventoryContext,
} from "../context/inventory-context";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

const schema = yup.object().shape({
  sku: yup.string().required("Campo requerido"),
});

type InventorySearchFormProps = {};

const InventorySearchForm: FC<InventorySearchFormProps> = () => {
  const { t } = useTranslation();
  const {
    selectedStore,
    selectedFfmCenter,
    setSelectedStore,
    setIsFilterDataLoading,
    setSearch,
    setSelectedFfmCenter,
    setSku,
  } = useInventoryContext();
  const { getStoresByTenant } = useStoresHandler();
  const { getFfmcentersByTenant } = useFfmcentersHandler();
  const [stores, setStores] = useState<Store[]>([]);
  const allFfmcenters = useRef<any[]>([]);
  const [ffmcenters, setFfmcenters] = useState<FulfillmentCenter[]>([]);

  const cancels: any = { cancels: [] };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      sku: "",
    },
  });

  const fetchStores = async () => {
    const [res, canceler1] = getStoresByTenant();
    cancels.cancels = canceler1;

    try {
      const r = await res;
      const fetchedStores = r.data?.data || [];
      setStores(fetchedStores);
      setSelectedStore(fetchedStores[0] as Store);
      await fetchFfmcenters();
    } catch (error) {
      console.log("Error fetching stores", error);
      setIsFilterDataLoading(false);
    }
  };

  const fetchFfmcenters = async () => {
    try {
      const [res, canceler1] = getFfmcentersByTenant();
      cancels.cancels = canceler1;

      const r = await res;
      const ffmcentersData: any[] = r.data?.data || [];
      allFfmcenters.current = ffmcentersData;
    } catch (error) {
      console.log("Error fetching ffmcenters", error);
    }
    setIsFilterDataLoading(false);
  };

  const onSubmit = (data: any) => {
    setSku(data.sku);
    setSearch(true);
  };

  const onClear = () => {
    reset();
    setSearch(false);
    setSelectedFfmCenter(undefined);
  };

  useEffect(() => {
    if (selectedStore) {
      if (allFfmcenters.current.length > 0) {
        setFfmcenters(
          allFfmcenters.current.filter(
            (ffmcenter) => ffmcenter?.store?.storeId === selectedStore.storeId
          )
        );
      }

      setSelectedFfmCenter(undefined);
    }
  }, [selectedStore, allFfmcenters.current]);

  useEffect(() => {
    fetchStores();
  }, []);

  return (
    <>
      <Grid item xs={12}>
        <SearchFilterTitle title={t("Report.SearchFilterTitle")} />
      </Grid>

      <Grid container component="form" onSubmit={handleSubmit(onSubmit)}>
        <Grid item xs={12} sm={6} xl={3} md={4} className="caja-form">
          <FormInputLabel text="Tienda" />
          <TextField
            select
            variant="outlined"
            size="small"
            fullWidth
            onChange={(e) => {
              const store = stores.find(
                (store) => store.storeId === parseInt(e.target.value)
              );
              if (!store) {
                return;
              }
              setSelectedStore({ ...store });
            }}
            value={selectedStore?.storeId || ""}>
            {stores.map((store) => (
              <MenuItem key={store.storeId} value={store.storeId}>
                {store.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} xl={3} md={5} className="caja-form">
          <FormInputLabel text="Bodega" />
          <TextField
            select
            variant="outlined"
            size="small"
            fullWidth
            onChange={(e) => {
              const ffmcenter = ffmcenters.find(
                (ffmcenter) =>
                  ffmcenter.fulfillmentCenterId === parseInt(e.target.value)
              );
              if (!ffmcenter) {
                return;
              }
              setSelectedFfmCenter({ ...ffmcenter });
            }}
            defaultValue=""
            value={selectedFfmCenter?.fulfillmentCenterId || ""}
            SelectProps={{
              MenuProps: {
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
                PaperProps: {
                  style: {
                    maxHeight: 220,
                    maxWidth: 300,
                    fontSize: "9px",
                  },
                },
              },
            }}>
            {ffmcenters.map((ffmcenter) => (
              <MenuItem
                key={ffmcenter.fulfillmentCenterId}
                value={ffmcenter.fulfillmentCenterId}>
                {ffmcenter.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} xl={3} md={3} className="caja-form">
          <FormInputLabel text="Sku" required />
          <Controller
            control={control}
            name="sku"
            render={({ field: { onChange, value } }) => (
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                value={value}
                onChange={onChange}
                error={errors.sku ? true : false}
                helperText={errors.sku?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <RequiredLabel />
        </Grid>

        <Grid item xs={12} sm={12} xl={12} md={12} className="caja-form">
          <Button type="submit" value="Search" name="Search">
            Buscar
          </Button>

          {(selectedFfmCenter || watch("sku") !== "") && (
            <span style={{ marginLeft: "20px" }}>
              <Button type="button" variant="text" onClick={onClear}>
                {t("OrderManagement.SearchClean")}
              </Button>
            </span>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default InventorySearchForm;
